import type { Played } from '../chifoumi/rules';
import { err, ok, type Result } from '../lib/result';

export const PROTOCOL_VERSION = 6;
/** Au-delà, le message est rejeté sans être décodé (taille sûre pour une DataChannel). */
export const MAX_MESSAGE_BYTES = 64 * 1024;
export const MAX_REPLAY_CHUNKS = 512;
/** Photo du geste (chifoumi), JPEG en base64 : tient dans un seul message. */
export const MAX_SNAPSHOT_CHARS = 48 * 1024;
const MAX_ROUND = 1_000_000;

/** Issue d'une manche, du point de vue de l'hôte (arbitre). */
export type RoundOutcome = 'hostLost' | 'guestLost' | 'tie' | 'draw' | 'replay';
export type VoteVerdict = 'defeat' | 'replay';
/** Ce qui fait perdre : rire ou cligner des yeux. */
export type GameMode = 'laugh' | 'blink';
/** Le jeu de la partie, choisi par l'hôte. */
export type GameKind = 'barbichette' | 'chifoumi' | 'smiley';

/**
 * `round` est un identifiant de tentative, croissant, identique des deux côtés
 * (une manche rejouée a un nouvel identifiant) : il écarte les messages périmés.
 */
export type Message =
  /** Jeu et mode : font foi ceux de l'hôte. */
  | {
      readonly type: 'hello';
      readonly version: number;
      readonly game: GameKind;
      readonly mode: GameMode;
      /** Imite ce smiley : graine du tirage des smileys (l'invité tire les mêmes). */
      readonly seed?: number;
    }
  | { readonly type: 'ready' }
  | { readonly type: 'calibrated'; readonly round: number }
  | { readonly type: 'grab'; readonly round: number }
  | { readonly type: 'laugh'; readonly round: number; readonly t: number; readonly score: number }
  /** Chifoumi : geste verrouillé au « MI » (`none` : aucune main lisible). */
  | { readonly type: 'throw'; readonly round: number; readonly gesture: Played }
  /** Imite ce smiley : meilleur score tenu (0..100) à la fin des 10 s. */
  | { readonly type: 'imitation'; readonly round: number; readonly score: number }
  /** Chifoumi : photo (JPEG, base64) prise au verrouillage du geste, révélée au résultat. */
  | { readonly type: 'snapshot'; readonly round: number; readonly data: string }
  | { readonly type: 'faceLost'; readonly round: number; readonly t: number }
  | { readonly type: 'pause'; readonly round: number }
  | { readonly type: 'resume'; readonly round: number }
  | { readonly type: 'vote'; readonly round: number; readonly verdict: VoteVerdict }
  | { readonly type: 'verdict'; readonly round: number; readonly outcome: RoundOutcome }
  | { readonly type: 'heartbeat'; readonly t: number }
  | { readonly type: 'ping'; readonly id: number; readonly t: number }
  | { readonly type: 'pong'; readonly id: number; readonly t: number; readonly peerT: number }
  | {
      readonly type: 'replayChunk';
      readonly round: number;
      readonly seq: number;
      readonly total: number;
      /** Base64. */
      readonly data: string;
    }
  | { readonly type: 'rematch' };

export type MessageType = Message['type'];

type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const isTime = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0;
const isInt = (v: unknown, max: number): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= max;
const isRound = (v: unknown): v is number => isInt(v, MAX_ROUND);
const isScore = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1;
const oneOf =
  <T extends string>(...values: T[]) =>
  (v: unknown): v is T =>
    typeof v === 'string' && (values as string[]).includes(v);
const isOutcome = oneOf<RoundOutcome>('hostLost', 'guestLost', 'tie', 'draw', 'replay');
const isVote = oneOf<VoteVerdict>('defeat', 'replay');
const isMode = oneOf<GameMode>('laugh', 'blink');
const isGame = oneOf<GameKind>('barbichette', 'chifoumi', 'smiley');
const SEED_MAX = 2 ** 31 - 1;
const isPlayed = oneOf<Played>('rock', 'paper', 'scissors', 'none');
const isBase64 = (v: unknown): v is string =>
  typeof v === 'string' && /^[A-Za-z0-9+/]*={0,2}$/.test(v) && v.length % 4 === 0;

/** Valide un objet déjà décodé et le recopie champ par champ (les champs inconnus sont ignorés). */
function validate(m: Obj): Message | null {
  switch (m['type']) {
    case 'hello': {
      // Sans jeu (version 2) : barbichette, pour que l'écart de version soit détecté et affiché.
      const game = m['game'] ?? 'barbichette';
      const seed = m['seed'];
      if (!isInt(m['version'], MAX_ROUND) || !isGame(game) || !isMode(m['mode'])) return null;
      if (seed === undefined)
        return { type: 'hello', version: m['version'], game, mode: m['mode'] };
      return isInt(seed, SEED_MAX)
        ? { type: 'hello', version: m['version'], game, mode: m['mode'], seed }
        : null;
    }
    case 'imitation':
      return isRound(m['round']) && isInt(m['score'], 100)
        ? { type: 'imitation', round: m['round'], score: m['score'] }
        : null;
    case 'snapshot':
      return isRound(m['round']) && isBase64(m['data']) && m['data'].length <= MAX_SNAPSHOT_CHARS
        ? { type: 'snapshot', round: m['round'], data: m['data'] }
        : null;
    case 'throw':
      return isRound(m['round']) && isPlayed(m['gesture'])
        ? { type: 'throw', round: m['round'], gesture: m['gesture'] }
        : null;
    case 'ready':
      return { type: 'ready' };
    case 'rematch':
      return { type: 'rematch' };
    case 'calibrated':
    case 'grab':
    case 'pause':
    case 'resume':
      return isRound(m['round']) ? { type: m['type'], round: m['round'] } : null;
    case 'laugh':
      return isRound(m['round']) && isTime(m['t']) && isScore(m['score'])
        ? { type: 'laugh', round: m['round'], t: m['t'], score: m['score'] }
        : null;
    case 'faceLost':
      return isRound(m['round']) && isTime(m['t'])
        ? { type: 'faceLost', round: m['round'], t: m['t'] }
        : null;
    case 'vote':
      return isRound(m['round']) && isVote(m['verdict'])
        ? { type: 'vote', round: m['round'], verdict: m['verdict'] }
        : null;
    case 'verdict':
      return isRound(m['round']) && isOutcome(m['outcome'])
        ? { type: 'verdict', round: m['round'], outcome: m['outcome'] }
        : null;
    case 'heartbeat':
      return isTime(m['t']) ? { type: 'heartbeat', t: m['t'] } : null;
    case 'ping':
      return isInt(m['id'], Number.MAX_SAFE_INTEGER) && isTime(m['t'])
        ? { type: 'ping', id: m['id'], t: m['t'] }
        : null;
    case 'pong':
      return isInt(m['id'], Number.MAX_SAFE_INTEGER) && isTime(m['t']) && isTime(m['peerT'])
        ? { type: 'pong', id: m['id'], t: m['t'], peerT: m['peerT'] }
        : null;
    case 'replayChunk': {
      const { round, seq, total, data } = m;
      return isRound(round) &&
        isInt(total, MAX_REPLAY_CHUNKS) &&
        total > 0 &&
        isInt(seq, total - 1) &&
        isBase64(data)
        ? { type: 'replayChunk', round, seq, total, data }
        : null;
    }
    default:
      return null;
  }
}

/** Décode et valide un message reçu. Ne lève jamais d'exception. */
export function parseMessage(raw: unknown): Result<Message> {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    if (raw.length > MAX_MESSAGE_BYTES) return err('message trop gros');
    try {
      value = JSON.parse(raw);
    } catch {
      return err('JSON invalide');
    }
  }
  if (!isObj(value)) return err('objet attendu');
  const message = validate(value);
  return message ? ok(message) : err(`message invalide (${String(value['type'])})`);
}

export function encodeMessage(message: Message): string {
  return JSON.stringify(message);
}
